import { t } from "@/lib/i18n/interpolate";
import {
  asClause,
  fillFrame,
  indefiniteArticle,
  joinSeries,
  properWords,
  stripEnd,
  tidy,
} from "@/lib/tool-kit/essay/sentence";

/**
 * SOAPSTone notes and rhetorical-analysis thesis frames.
 *
 * SOAPSTone — Speaker, Occasion, Audience, Purpose, Subject, Tone — is a reading
 * mnemonic from AP English classrooms. The notes are the reader's own words, laid out
 * per element; the thesis frames combine them into the sentence an AP-style rhetorical
 * analysis opens with: who, in what situation, makes which choices, to what end.
 */

export type SoapField =
  | "title"
  | "speaker"
  | "speakerContext"
  | "occasion"
  | "audience"
  | "purpose"
  | "subject"
  | "tone"
  | "choices";

export type SoapInput = Record<SoapField, string>;

export type TextType = "speech" | "essay" | "letter" | "other";

export const TEXT_TYPES: readonly TextType[] = [
  "speech",
  "essay",
  "letter",
  "other",
];

/** Tone words grouped by the attitude they express. */
export const SOAPSTONE_TONE_WORDS = {
  approving: [
    "admiring",
    "affectionate",
    "appreciative",
    "celebratory",
    "compassionate",
    "confident",
    "earnest",
    "enthusiastic",
    "hopeful",
    "inspiring",
    "jubilant",
    "optimistic",
    "proud",
    "respectful",
    "reverent",
    "sincere",
    "sympathetic",
    "uplifting",
  ],
  critical: [
    "accusatory",
    "angry",
    "bitter",
    "condemning",
    "condescending",
    "contemptuous",
    "critical",
    "cynical",
    "disapproving",
    "disdainful",
    "frustrated",
    "indignant",
    "mocking",
    "outraged",
    "resentful",
    "scathing",
    "scornful",
  ],
  ironic: [
    "amused",
    "droll",
    "facetious",
    "flippant",
    "ironic",
    "irreverent",
    "lighthearted",
    "mock-serious",
    "playful",
    "sarcastic",
    "sardonic",
    "satirical",
    "teasing",
    "whimsical",
    "wry",
  ],
  somber: [
    "despairing",
    "elegiac",
    "grave",
    "grieving",
    "melancholy",
    "mournful",
    "nostalgic",
    "pessimistic",
    "poignant",
    "regretful",
    "resigned",
    "solemn",
    "somber",
    "sorrowful",
    "wistful",
  ],
  objective: [
    "analytical",
    "candid",
    "clinical",
    "detached",
    "didactic",
    "factual",
    "formal",
    "impartial",
    "informative",
    "instructive",
    "matter-of-fact",
    "measured",
    "objective",
    "reflective",
    "straightforward",
  ],
  forceful: [
    "assertive",
    "authoritative",
    "commanding",
    "defiant",
    "determined",
    "emphatic",
    "fervent",
    "forthright",
    "impassioned",
    "insistent",
    "resolute",
    "rousing",
    "stern",
    "urgent",
    "zealous",
  ],
  uneasy: [
    "anxious",
    "apprehensive",
    "cautionary",
    "concerned",
    "conflicted",
    "doubtful",
    "fearful",
    "foreboding",
    "hesitant",
    "ominous",
    "skeptical",
    "tentative",
    "uncertain",
    "uneasy",
    "wary",
  ],
} as const;

/**
 * English thesis frames. `{addressing}` comes from the text type ("speaking to"),
 * `{atOccasion}` adds "at" unless the occasion already opens with a preposition, and
 * `{aTone}` is the tone list with its article ("a solemn and resolute").
 */
export const SOAPSTONE_THESIS_FRAMES = [
  "In {title}, {speaker} uses {choices} to {purpose}.",
  "{addressing} {audience} {atOccasion}, {speaker} adopts {aTone} tone in order to {purpose}.",
  "Through {choices}, {speaker} addresses {subject} in {aTone} tone, aiming to {purpose}.",
  "{speaker} begins by ___, shifts to ___, and ends by ___, a progression designed to {purpose}.",
] as const;

const RE_TONE_SPLIT = /\s*(?:,|;|\/|\band\b|&)\s*/i;
const RE_LEADING_TO = /^(?:in order to|so as to|to)\s+/i;
const RE_LEADING_PREPOSITION =
  /^(?:at|during|on|in|after|before|following|for|amid|within)\b/i;

/** "solemn, reverent and resolute" → ["solemn", "reverent", "resolute"]. */
export function parseTones(text: string): string[] {
  const seen = new Set<string>();
  const tones: string[] = [];
  for (const part of text.split(RE_TONE_SPLIT)) {
    const tone = stripEnd(part).toLowerCase();
    if (tone !== "" && !seen.has(tone)) {
      seen.add(tone);
      tones.push(tone);
    }
  }
  return tones;
}

/** Add a tone word to the field, or take it out if it is already there. */
export function toggleTone(text: string, word: string): string {
  const tones = parseTones(text);
  const next = tones.includes(word)
    ? tones.filter((tone) => tone !== word)
    : [...tones, word];
  return next.join(", ");
}

/** The purpose as the verb phrase that follows "to": "to honor the dead" → "honor the dead". */
export function purposePhrase(text: string): string {
  return stripEnd(tidy(text).replace(RE_LEADING_TO, ""));
}

export interface SoapLabels {
  /** "speaking to", "writing for"… by text type. */
  addressing: Readonly<Record<TextType, string>>;
  /** Bracketed stand-ins for empty fields: "[title]". */
  missing: Readonly<Record<string, string>>;
  /** The conjunction for the tone list: "and". */
  and: string;
  /** The preposition before an occasion: "at". */
  at: string;
}

function orMissing(value: string, key: string, labels: SoapLabels): string {
  return value === "" ? (labels.missing[key] ?? `[${key}]`) : value;
}

/** The values the thesis frames draw on, with stand-ins for anything left empty. */
export function soapValues(
  input: SoapInput,
  textType: TextType,
  labels: SoapLabels
): Record<string, string> {
  const proper = properWords(Object.values(input));
  const tones = parseTones(input.tone);
  const toneText = orMissing(joinSeries(tones, labels.and), "tone", labels);
  const occasion = asClause(input.occasion, proper);
  let atOccasion = orMissing(occasion, "occasion", labels);
  if (occasion !== "" && !RE_LEADING_PREPOSITION.test(occasion)) {
    atOccasion = `${labels.at} ${occasion}`;
  }
  return {
    title: orMissing(stripEnd(input.title), "title", labels),
    speaker: orMissing(stripEnd(input.speaker), "speaker", labels),
    audience: orMissing(asClause(input.audience, proper), "audience", labels),
    atOccasion,
    purpose: orMissing(
      asClause(purposePhrase(input.purpose), proper),
      "purpose",
      labels
    ),
    subject: orMissing(asClause(input.subject, proper), "subject", labels),
    choices: orMissing(asClause(input.choices, proper), "choices", labels),
    aTone: `${indefiniteArticle(toneText)} ${toneText}`,
    addressing: labels.addressing[textType],
  };
}

/** Each thesis frame, filled. Fields left empty show as bracketed stand-ins. */
export function buildThesisFrames(
  input: SoapInput,
  textType: TextType,
  labels: SoapLabels,
  frames: readonly string[] = SOAPSTONE_THESIS_FRAMES
): string[] {
  const values = soapValues(input, textType, labels);
  return frames
    .map((frame) => fillFrame(frame, values))
    .filter((text): text is string => text !== null);
}

/** Speaker and purpose are what every thesis frame needs. */
export function canFrameThesis(input: SoapInput): boolean {
  return input.speaker.trim() !== "" && input.purpose.trim() !== "";
}

/* ------------------------------------------------------------------ notes */

/** The note rows, in SOAPSTone order, then the choices the essay will analyze. */
export const NOTE_ROWS = [
  "speaker",
  "occasion",
  "audience",
  "purpose",
  "subject",
  "tone",
  "choices",
] as const;

export type NoteRow = (typeof NOTE_ROWS)[number];

/** The speaker row folds in who they are: "Abraham Lincoln — the sixteenth president…". */
export function noteText(input: SoapInput, row: NoteRow): string {
  if (row === "speaker") {
    return [input.speaker, input.speakerContext]
      .map(tidy)
      .filter((part) => part !== "")
      .join(" — ");
  }
  return tidy(input[row]);
}

export interface NoteStrings {
  notesTitle: string;
  notesTitleUntitled: string;
  noteLine: string;
  notesEmpty: string;
  noteLabels: Readonly<Record<NoteRow, string>>;
}

/** The notes as plain text for the clipboard: a heading, then one line per element. */
export function notesText(input: SoapInput, strings: NoteStrings): string {
  const title = tidy(input.title);
  const heading =
    title === ""
      ? strings.notesTitleUntitled
      : t(strings.notesTitle, { title });
  const lines = NOTE_ROWS.map((row) =>
    t(strings.noteLine, {
      label: strings.noteLabels[row],
      text: noteText(input, row) || strings.notesEmpty,
    })
  );
  return [heading, ...lines].join("\n");
}
